import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { PageHeader } from "@/components/admin/AdminLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { JHARKHAND_DISTRICTS, blocksFor } from "@/data/jharkhand";
import { 
  BadgeCheck, 
  Crown, 
  Ban, 
  EyeOff, 
  Eye, 
  ShieldCheck, 
  Search, 
  ChevronLeft, 
  ChevronRight, 
  UserCheck, 
  Trash2, 
  Award, 
  Mail, 
  MessageSquare,
  X,
  FileText,
  User,
  Activity,
  Send,
  Pencil,
  FileDown
} from "lucide-react";
import { toast } from "sonner";

const PAGE = 20;

export default function AdminUsers() {
  const [searchParams] = useSearchParams();
  const [q, setQ] = useState(searchParams.get("q") || searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "all");
  const [gender, setGender] = useState(searchParams.get("gender") || "all");
  const [district, setDistrict] = useState("all");
  const [block, setBlock] = useState("all");
  const [plan, setPlan] = useState("all");

  const [data, setData] = useState({ results: [], total: 0 });
  const [packages, setPackages] = useState([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);

  // Bulk actions state
  const [selectedIds, setSelectedIds] = useState([]);

  // Modal states
  const [viewingUser, setViewingUser] = useState(null);
  const [viewingUserFull, setViewingUserFull] = useState(null);
  const [modalTab, setModalTab] = useState("view"); // view, edit, message
  const [editForm, setEditForm] = useState({});
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [smsMessage, setSmsMessage] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [sendingComm, setSendingComm] = useState(false);
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);

  // Load packages for filtering
  useEffect(() => {
    api.get("/packages/public")
      .then(({ data }) => setPackages(data))
      .catch((e) => console.error("Error loading packages:", e));
  }, []);

  const load = useCallback(async (toPage = 0) => {
    setLoading(true);
    setSelectedIds([]); // Clear selection when loading new page/filters
    try {
      const params = { 
        q: q || undefined, 
        status, 
        gender: gender !== "all" ? gender : undefined,
        district: district !== "all" ? district : undefined,
        block: block !== "all" ? block : undefined,
        plan: plan !== "all" ? plan : undefined,
        skip: toPage * PAGE, 
        limit: PAGE 
      };
      const { data } = await api.get("/admin/users", { params });
      setData(data); 
      setPage(toPage);
    } catch (e) { 
      toast.error(formatError(e)); 
    } finally { 
      setLoading(false); 
    }
  }, [q, status, gender, district, block, plan]);

  useEffect(() => { 
    load(0); 
    // eslint-disable-next-line
  }, [status, gender, district, block, plan]);

  // Sync gender param from URL to state
  useEffect(() => {
    const genderParam = searchParams.get("gender") || "all";
    if (genderParam !== gender) {
      setGender(genderParam);
    }
  }, [searchParams]);

  // Auto-open modal if user_id query is passed
  useEffect(() => {
    const qParam = searchParams.get("q") || searchParams.get("search") || "";
    if (qParam && qParam.startsWith("user_") && data.results.length > 0) {
      const match = data.results.find(u => u.user_id === qParam);
      if (match) {
        openModal(match, "view");
      }
    }
    // eslint-disable-next-line
  }, [data.results]);

  const resetFilters = () => {
    setQ("");
    setStatus("all");
    setGender("all");
    setDistrict("all");
    setBlock("all");
    setPlan("all");
    setSelectedIds([]);
  };

  const exportToExcel = () => {
    const selectedUsers = data.results.filter((u) => selectedIds.includes(u.user_id));
    if (selectedUsers.length === 0) return;

    const headers = [
      "Profile ID", "Name", "Email", "Phone", "Gender", 
      "District", "Block", "Plan", "Status", "Joined Date"
    ];
    
    const rows = selectedUsers.map((u) => [
      u.profile_id || "",
      u.name || "",
      u.email || "",
      u.phone || "",
      u.gender || "",
      u.district || "",
      u.block || "",
      packages.find((p) => p.id === u.premium_plan)?.name || "Free Plan",
      u.banned ? "Banned" : u.hidden ? "Hidden" : "Active",
      u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : ""
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((val) => {
        const cleanVal = String(val).replace(/"/g, '""');
        return cleanVal.includes(",") || cleanVal.includes("\n") || cleanVal.includes('"') 
          ? `"${cleanVal}"` 
          : cleanVal;
      }).join(","))
    ].join("\n");

    // Prepend UTF-8 BOM so Excel opens Hindi/special characters correctly
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `hamarsathi_users_export_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const bulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirmMsg = `Are you sure you want to permanently delete the ${selectedIds.length} selected user(s)? This will also delete their messages, interests, and profile logs. This action cannot be undone.`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const { data: res } = await api.post("/admin/users/bulk-delete", {
        user_ids: selectedIds
      });
      if (res.ok) {
        toast.success(`Successfully deleted ${res.deleted_count} user(s).`);
        setSelectedIds([]);
        load(0);
      }
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const act = async (id, action) => {
    const labels = { 
      verify: "User verified", 
      unverify: "Verification removed", 
      hide: "User hidden", 
      unhide: "User unhidden", 
      ban: "User banned", 
      unban: "User unbanned", 
      premium: "Marked premium", 
      unpremium: "Premium removed",
      founding: "Marked as Founding Member",
      unfounding: "Removed Founding status"
    };
    try { 
      await api.post(`/admin/users/${id}/action?action=${action}`); 
      toast.success(labels[action] || "Updated"); 
      load(page);
      
      // Update full detail modal if active
      if (viewingUserFull && viewingUserFull.user_id === id) {
        fetchFullProfile(id);
      }
    } catch (e) { 
      toast.error(formatError(e)); 
    }
  };

  const impersonate = async (id) => {
    // Open a new tab immediately to bypass browser popup blockers
    const newTab = window.open("about:blank", "_blank");
    if (newTab) {
      newTab.document.write("<div style='font-family: sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; color: #6B1A24;'><h3>Logging in as user... Please wait.</h3></div>");
    }
    try {
      const { data } = await api.post(`/admin/users/${id}/impersonate`);
      if (data.token) {
        toast.success("Masquerading as user...");
        if (newTab) {
          newTab.location.href = `/app?token=${data.token}`;
        } else {
          window.open(`/app?token=${data.token}`, "_blank");
        }
      } else {
        if (newTab) newTab.close();
      }
    } catch (e) {
      if (newTab) newTab.close();
      toast.error(formatError(e));
    }
  };

  const deleteUser = async (id, name) => {
    if (!window.confirm(`Are you sure you want to permanently delete the user "${name}"? This action cannot be undone and will purge all their matching data.`)) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("User permanently deleted");
      if (viewingUser && viewingUser.user_id === id) {
        closeModal();
      }
      load(page);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const fetchFullProfile = async (id) => {
    try {
      const { data } = await api.get(`/admin/users/${id}`);
      setViewingUserFull(data);
      setEditForm(data ? { ...data, password: data.password_plain || "" } : {});
    } catch (e) {
      toast.error("Failed to load user full profile: " + formatError(e));
    }
  };

  const openModal = (user, tab = "view") => {
    setViewingUser(user);
    setViewingUserFull(null);
    setModalTab(tab);
    setEmailSubject("");
    setEmailBody("");
    setSmsMessage("");
    setShowPasswordEdit(false);
    fetchFullProfile(user.user_id);
  };

  const closeModal = () => {
    setViewingUser(null);
    setViewingUserFull(null);
    setShowPasswordEdit(false);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put(`/admin/users/${viewingUserFull.user_id}`, editForm);
      toast.success("Profile saved successfully");
      load(page);
      fetchFullProfile(viewingUserFull.user_id);
      setModalTab("view");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSavingProfile(false);
    }
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Subject and message body are required");
      return;
    }
    setSendingComm(true);
    try {
      await api.post(`/admin/users/${viewingUserFull.user_id}/send-email`, {
        subject: emailSubject,
        body: emailBody
      });
      toast.success("Email sent successfully!");
      setEmailSubject("");
      setEmailBody("");
    } catch (e) {
      toast.error("Email delivery failed: " + formatError(e));
    } finally {
      setSendingComm(false);
    }
  };

  const sendSMS = async () => {
    if (!smsMessage.trim()) {
      toast.error("SMS message is required");
      return;
    }
    setSendingComm(true);
    try {
      await api.post(`/admin/users/${viewingUserFull.user_id}/send-sms`, {
        message: smsMessage
      });
      toast.success("SMS request sent!");
      setSmsMessage("");
    } catch (e) {
      toast.error("SMS delivery failed: " + formatError(e));
    } finally {
      setSendingComm(false);
    }
  };

  const getPlanLabel = (planId) => {
    if (!planId) return <span className="text-xs text-slate-400">None</span>;
    const pkg = packages.find((p) => p.id === planId);
    if (!pkg) return <span className="text-xs text-slate-500">Free Plan</span>;
    return (
      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${pkg.price === 0 ? "bg-slate-100 text-slate-600" : "bg-[#6B1A24]/10 text-[#6B1A24] border border-[#6B1A24]/20"}`}>
        {pkg.name}
      </span>
    );
  };

  const totalPages = Math.max(1, Math.ceil(data.total / PAGE));

  return (
    <div className="pb-6 text-left">
      <PageHeader title="User Management" subtitle={`${data.total} registered members`} />

      {/* Advanced Filter Box */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-3.5 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2.5">
          {/* Search bar */}
          <div className="relative min-w-0">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(0)} placeholder="Search name, email, phone..." className="pl-9 h-9 text-xs" data-testid="admin-users-search" />
          </div>

          {/* District Select */}
          <div className="min-w-0">
            <Select value={district} onValueChange={(val) => { setDistrict(val); setBlock("all"); }}>
              <SelectTrigger className="h-9 text-xs" data-testid="admin-users-district">
                <SelectValue placeholder="All Districts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Districts</SelectItem>
                {JHARKHAND_DISTRICTS.map((d) => (
                  <SelectItem key={d} value={d}>{d}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Block Select */}
          <div className="min-w-0">
            <Select value={block} onValueChange={setBlock} disabled={!district || district === "all"}>
              <SelectTrigger className="h-9 text-xs" data-testid="admin-users-block">
                <SelectValue placeholder="All Blocks" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Blocks</SelectItem>
                {district && district !== "all" && blocksFor(district).map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Gender Select */}
          <div className="min-w-0">
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger className="h-9 text-xs" data-testid="admin-users-gender">
                <SelectValue placeholder="All Genders" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Genders</SelectItem>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Status Select */}
          <div className="min-w-0">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 text-xs" data-testid="admin-users-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="verified">✓ Verified</SelectItem>
                <SelectItem value="unverified_photos">📷 Photo Queue (Unverified)</SelectItem>
                <SelectItem value="unverified">Unverified</SelectItem>
                <SelectItem value="premium">⭐ Premium</SelectItem>
                <SelectItem value="banned">🚫 Banned</SelectItem>
                <SelectItem value="hidden">Hidden</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Plan Select */}
          <div className="min-w-0">
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger className="h-9 text-xs" data-testid="admin-users-plan">
                <SelectValue placeholder="All Plans" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Plans</SelectItem>
                <SelectItem value="none">No Plan</SelectItem>
                {packages.map((pkg) => (
                  <SelectItem key={pkg.id} value={pkg.id}>{pkg.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-2.5">
          <Button variant="outline" size="sm" className="h-8 text-xs px-3" onClick={resetFilters}>Clear Filters</Button>
          <Button onClick={() => load(0)} size="sm" className="h-8 text-xs px-3 bg-[#6B1A24] hover:bg-[#52141C]" data-testid="admin-users-apply">Apply Filters</Button>
        </div>
      </div>

      {/* User Table Card */}
      {selectedIds.length > 0 && (
        <div className="flex items-center justify-between bg-slate-50 border border-slate-200 px-3.5 py-2.5 rounded-lg mb-3 text-xs shadow-sm transition-all animate-in fade-in duration-200">
          <div className="font-semibold text-slate-700">
            Selected {selectedIds.length} user{selectedIds.length > 1 ? "s" : ""}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportToExcel}
              className="h-8 text-xs px-3 flex items-center gap-1.5 text-slate-700 border-slate-300 hover:bg-slate-100"
            >
              <FileDown className="w-3.5 h-3.5 text-slate-500" /> Export to Excel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={bulkDelete}
              className="h-8 text-xs px-3 flex items-center gap-1.5"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete Selected
            </Button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto max-w-full shadow-sm">
        <table className="w-full min-w-[980px] table-auto">
          <thead>
            <tr className="bg-slate-50/70 border-b border-slate-100">
              <th className="px-3 py-2 text-left w-9">
                <input
                  type="checkbox"
                  className="rounded border-slate-300 text-[#6B1A24] focus:ring-[#6B1A24] cursor-pointer"
                  checked={data.results.length > 0 && selectedIds.length === data.results.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedIds(data.results.map((u) => u.user_id));
                    } else {
                      setSelectedIds([]);
                    }
                  }}
                />
              </th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[200px]">Name</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[150px]">District & Block</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[100px]">Plan</th>
              <th className="px-3 py-2 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[90px]">Status</th>
              <th className="px-3 py-2 text-right text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[340px] w-[340px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">Loading directory...</td></tr>
            ) : data.results.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No users found.</td></tr>
            ) : data.results.map((u) => (
              <tr key={u.user_id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors" data-testid={`admin-users-row-${u.user_id}`}>
                <td className="px-3 py-2 w-9">
                  <input
                    type="checkbox"
                    className="rounded border-slate-300 text-[#6B1A24] focus:ring-[#6B1A24] cursor-pointer"
                    checked={selectedIds.includes(u.user_id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedIds((prev) => [...prev, u.user_id]);
                      } else {
                        setSelectedIds((prev) => prev.filter((id) => id !== u.user_id));
                      }
                    }}
                  />
                </td>
                <td className="px-3 py-2 min-w-[200px]">
                  <div className="font-semibold text-sm text-slate-800 flex flex-wrap items-center gap-1.5">
                    {u.name} 
                    {u.role === "admin" && (
                      <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded-md font-semibold">Admin</span>
                    )} 
                    {u.is_verified && (
                      <BadgeCheck className="w-3.5 h-3.5 text-emerald-600" />
                    )} 
                    {u.is_premium && (
                      <Crown className="w-3.5 h-3.5 text-[#E69500]" />
                    )}
                    {u.is_founding_member && (
                      <span className="inline-flex items-center gap-0.5 text-[9px] bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded font-bold uppercase tracking-wider" title="Founding Member">
                        Founding
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 max-w-[260px] truncate" title={u.email}>{u.email}{u.phone ? ` · ${u.phone}` : ""}</div>
                </td>
                <td className="px-3 py-2 text-xs text-slate-600 min-w-[150px]">
                  <div>{[u.block, u.district].filter(Boolean).join(", ") || "—"}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">Joined: {u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</div>
                </td>
                <td className="px-3 py-2 min-w-[100px]">
                  {getPlanLabel(u.premium_plan)}
                </td>
                <td className="px-3 py-2 min-w-[90px]">
                  {u.banned ? (
                    <span className="text-xs bg-red-50 text-red-600 px-2.5 py-0.5 rounded-full border border-red-100 font-medium">Banned</span>
                  ) : u.hidden ? (
                    <span className="text-xs bg-amber-50 text-amber-600 px-2.5 py-0.5 rounded-full border border-amber-100 font-medium">Hidden</span>
                  ) : (
                    <span className="text-xs bg-emerald-50 text-emerald-600 px-2.5 py-0.5 rounded-full border border-emerald-100 font-medium">Active</span>
                  )}
                </td>
                <td className="px-3 py-2 min-w-[340px] w-[340px] text-right">
                  <div className="flex items-center justify-end gap-1 flex-nowrap">
                    <IconBtn title="Login as User" onClick={() => impersonate(u.user_id)} testid={`admin-impersonate-${u.user_id}`}><UserCheck className="w-3.5 h-3.5 text-blue-600" /></IconBtn>
                    <IconBtn title="View & Edit Full Profile" onClick={() => openModal(u, "view")} testid={`admin-view-${u.user_id}`}><FileText className="w-3.5 h-3.5 text-slate-600" /></IconBtn>
                    <IconBtn title="Send Email/SMS" onClick={() => openModal(u, "message")} testid={`admin-msg-${u.user_id}`}><Mail className="w-3.5 h-3.5 text-slate-600" /></IconBtn>
                    
                    {!u.is_verified ? (
                      <IconBtn title="Verify" onClick={() => act(u.user_id, "verify")} testid={`admin-verify-${u.user_id}`}><ShieldCheck className="w-3.5 h-3.5 text-slate-500" /></IconBtn>
                    ) : (
                      <IconBtn title="Unverify" onClick={() => act(u.user_id, "unverify")} testid={`admin-unverify-${u.user_id}`}><BadgeCheck className="w-3.5 h-3.5 text-emerald-600" /></IconBtn>
                    )}

                    {!u.is_founding_member ? (
                      <IconBtn title="Add Founding Badge" onClick={() => act(u.user_id, "founding")} testid={`admin-founding-${u.user_id}`}><Award className="w-3.5 h-3.5 text-slate-500" /></IconBtn>
                    ) : (
                      <IconBtn title="Remove Founding Badge" onClick={() => act(u.user_id, "unfounding")} testid={`admin-unfounding-${u.user_id}`}><Award className="w-3.5 h-3.5 text-amber-600" /></IconBtn>
                    )}

                    {!u.is_premium ? (
                      <IconBtn title="Mark Premium" onClick={() => act(u.user_id, "premium")} testid={`admin-premium-${u.user_id}`}><Crown className="w-3.5 h-3.5 text-slate-500" /></IconBtn>
                    ) : (
                      <IconBtn title="Remove Premium" onClick={() => act(u.user_id, "unpremium")} testid={`admin-unpremium-${u.user_id}`}><Crown className="w-3.5 h-3.5 text-[#E69500]" /></IconBtn>
                    )}

                    {u.hidden ? (
                      <IconBtn title="Unhide" onClick={() => act(u.user_id, "unhide")} testid={`admin-unhide-${u.user_id}`}><Eye className="w-3.5 h-3.5 text-slate-500" /></IconBtn>
                    ) : (
                      <IconBtn title="Hide" onClick={() => act(u.user_id, "hide")} testid={`admin-hide-${u.user_id}`}><EyeOff className="w-3.5 h-3.5 text-slate-500" /></IconBtn>
                    )}

                    {u.banned ? (
                      <IconBtn title="Unban" onClick={() => act(u.user_id, "unban")} testid={`admin-unban-${u.user_id}`}><Ban className="w-3.5 h-3.5 text-emerald-600" /></IconBtn>
                    ) : (
                      <IconBtn title="Ban / Block" danger onClick={() => act(u.user_id, "ban")} testid={`admin-ban-${u.user_id}`}><Ban className="w-3.5 h-3.5" /></IconBtn>
                    )}

                    <IconBtn title="Delete User" danger onClick={() => deleteUser(u.user_id, u.name)} testid={`admin-delete-${u.user_id}`}><Trash2 className="w-3.5 h-3.5" /></IconBtn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.total > PAGE && (
        <div className="flex items-center justify-center gap-3 mt-6">
          <Button variant="outline" size="sm" disabled={page === 0} onClick={() => load(page - 1)} data-testid="admin-users-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm text-slate-500">Page {page + 1} of {totalPages}</span>
          <Button variant="outline" size="sm" disabled={page + 1 >= totalPages} onClick={() => load(page + 1)} data-testid="admin-users-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}

      {/* View/Edit Profile & Messaging Dialog Modal */}
      {viewingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4 text-left">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 shrink-0 bg-slate-50">
              <div>
                <h2 className="text-lg font-bold text-[#2A0A0E] flex items-center gap-2">
                  {viewingUser.name || "Loading..."}
                  {viewingUserFull?.is_founding_member && (
                    <span className="text-[10px] bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded font-bold uppercase">Founding</span>
                  )}
                  {viewingUserFull?.is_premium && (
                    <span className="text-[10px] bg-yellow-50 text-yellow-700 border border-yellow-200 px-2 py-0.5 rounded font-bold uppercase">Premium</span>
                  )}
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">ID: {viewingUser.user_id}</p>
              </div>
              <button onClick={closeModal} className="p-1 rounded-full hover:bg-slate-200 transition-colors">
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            {/* Modal Tabs Selection */}
            <div className="flex border-b border-slate-100 px-6 bg-slate-50/50 shrink-0">
              <button 
                onClick={() => setModalTab("view")} 
                className={`py-2.5 px-4 font-semibold text-sm border-b-2 transition-all ${modalTab === "view" ? "border-[#6B1A24] text-[#6B1A24]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              >
                <span className="flex items-center gap-1.5"><User className="w-4 h-4" /> View Profile</span>
              </button>
              <button 
                onClick={() => setModalTab("edit")} 
                className={`py-2.5 px-4 font-semibold text-sm border-b-2 transition-all ${modalTab === "edit" ? "border-[#6B1A24] text-[#6B1A24]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              >
                <span className="flex items-center gap-1.5"><Pencil className="w-4 h-4" /> Edit Profile</span>
              </button>
              <button 
                onClick={() => setModalTab("message")} 
                className={`py-2.5 px-4 font-semibold text-sm border-b-2 transition-all ${modalTab === "message" ? "border-[#6B1A24] text-[#6B1A24]" : "border-transparent text-slate-500 hover:text-slate-700"}`}
              >
                <span className="flex items-center gap-1.5"><Mail className="w-4 h-4" /> Send Message</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="flex-1 overflow-y-auto p-6">
              {!viewingUserFull ? (
                <div className="py-20 text-center text-slate-400 text-sm">Loading full user data...</div>
              ) : (
                <>
                  {/* Tab Content: View Profile */}
                  {modalTab === "view" && (
                    <div className="space-y-6">
                      {/* Photo Section */}
                      {viewingUserFull.photos && viewingUserFull.photos.length > 0 && (
                        <div>
                          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Photos</h3>
                          <div className="flex flex-wrap gap-3">
                            {viewingUserFull.photos.map((ph, idx) => (
                              <img key={idx} src={ph} alt="" className="w-24 h-32 object-cover rounded-lg border border-slate-200 shadow-sm" />
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Info Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {/* Section: Basic info */}
                        <div className="border border-slate-100 rounded-xl p-4 bg-slate-50/50">
                          <h4 className="font-bold text-sm text-[#2A0A0E] mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                            <Activity className="w-4 h-4 text-[#6B1A24]" /> Basics
                          </h4>
                          <div className="space-y-2 text-xs">
                            <InfoRow label="Gender" val={viewingUserFull.gender} />
                            <InfoRow label="DOB" val={viewingUserFull.dob} />
                            <InfoRow label="Height" val={viewingUserFull.height_cm ? `${viewingUserFull.height_cm} cm` : null} />
                            <InfoRow label="Email" val={viewingUserFull.email} />
                            <InfoRow label="Phone" val={viewingUserFull.phone} />
                            <InfoRow label="Managed By" val={viewingUserFull.profile_managed_by} />
                            <InfoRow label="Registration IP" val={viewingUserFull.registration_ip} />
                            <InfoRow label="Last Login IP" val={viewingUserFull.last_login_ip} />
                          </div>
                        </div>

                        {/* Section: Community / Astro */}
                        <div className="border border-slate-100 rounded-xl p-4 bg-slate-50/50">
                          <h4 className="font-bold text-sm text-[#2A0A0E] mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                            <Award className="w-4 h-4 text-[#6B1A24]" /> Community & Astro
                          </h4>
                          <div className="space-y-2 text-xs">
                            <InfoRow label="Religion" val={viewingUserFull.religion} />
                            <InfoRow label="Caste" val={viewingUserFull.caste} />
                            <InfoRow label="Mother Tongue" val={viewingUserFull.mother_tongue} />
                            <InfoRow label="Rashi" val={viewingUserFull.rashi} />
                            <InfoRow label="Nakshatra" val={viewingUserFull.nakshatra} />
                            <InfoRow label="Manglik" val={viewingUserFull.manglik} />
                          </div>
                        </div>

                        {/* Section: Location & Work */}
                        <div className="border border-slate-100 rounded-xl p-4 bg-slate-50/50">
                          <h4 className="font-bold text-sm text-[#2A0A0E] mb-3 flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                            <Activity className="w-4 h-4 text-[#6B1A24]" /> Location & Profession
                          </h4>
                          <div className="space-y-2 text-xs">
                            <InfoRow label="District" val={viewingUserFull.district} />
                            <InfoRow label="Block" val={viewingUserFull.block} />
                            <InfoRow label="City" val={viewingUserFull.city} />
                            <InfoRow label="Village" val={viewingUserFull.village} />
                            <InfoRow label="Education" val={viewingUserFull.education} />
                            <InfoRow label="Occupation" val={viewingUserFull.occupation} />
                            <InfoRow label="Income" val={viewingUserFull.income} />
                          </div>
                        </div>
                      </div>

                      {/* Section: About */}
                      {viewingUserFull.about && (
                        <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                          <h4 className="font-semibold text-xs text-slate-400 uppercase tracking-wider mb-2">About Person</h4>
                          <p className="text-sm text-slate-600 italic">"{viewingUserFull.about}"</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tab Content: Edit Profile */}
                  {modalTab === "edit" && (
                    <div className="space-y-5">
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <Field label="Full Name">
                          <Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
                        </Field>

                        <Field label="Gender">
                          <Select value={editForm.gender || "none"} onValueChange={(val) => setEditForm({ ...editForm, gender: val === "none" ? "" : val })}>
                            <SelectTrigger><SelectValue placeholder="Select Gender" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Gender</SelectItem>
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                            </SelectContent>
                          </Select>
                        </Field>

                        <Field label="Date of Birth (YYYY-MM-DD)">
                          <Input value={editForm.dob || ""} onChange={(e) => setEditForm({ ...editForm, dob: e.target.value })} placeholder="e.g. 1996-08-25" />
                        </Field>

                        <Field label="Height (cm)">
                          <Input type="number" value={editForm.height_cm || ""} onChange={(e) => setEditForm({ ...editForm, height_cm: parseInt(e.target.value, 10) || null })} />
                        </Field>

                        <Field label="District">
                          <Select value={editForm.district || "none"} onValueChange={(val) => setEditForm({ ...editForm, district: val === "none" ? "" : val, block: "" })}>
                            <SelectTrigger><SelectValue placeholder="Select District" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select District</SelectItem>
                              {JHARKHAND_DISTRICTS.map((d) => (
                                <SelectItem key={d} value={d}>{d}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>

                        <Field label="Block">
                          <Select value={editForm.block || "none"} onValueChange={(val) => setEditForm({ ...editForm, block: val === "none" ? "" : val })}>
                            <SelectTrigger><SelectValue placeholder="Select Block" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Select Block</SelectItem>
                              {editForm.district && blocksFor(editForm.district).map((b) => (
                                <SelectItem key={b} value={b}>{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>

                        <Field label="City">
                          <Input value={editForm.city || ""} onChange={(e) => setEditForm({ ...editForm, city: e.target.value })} />
                        </Field>

                        <Field label="Village">
                          <Input value={editForm.village || ""} onChange={(e) => setEditForm({ ...editForm, village: e.target.value })} />
                        </Field>

                        <Field label="Religion">
                          <Input value={editForm.religion || ""} onChange={(e) => setEditForm({ ...editForm, religion: e.target.value })} />
                        </Field>

                        <Field label="Caste">
                          <Input value={editForm.caste || ""} onChange={(e) => setEditForm({ ...editForm, caste: e.target.value })} />
                        </Field>

                        <Field label="Education">
                          <Input value={editForm.education || ""} onChange={(e) => setEditForm({ ...editForm, education: e.target.value })} />
                        </Field>

                        <Field label="Occupation">
                          <Input value={editForm.occupation || ""} onChange={(e) => setEditForm({ ...editForm, occupation: e.target.value })} />
                        </Field>

                        <Field label="Annual Income">
                          <Input value={editForm.income || ""} onChange={(e) => setEditForm({ ...editForm, income: e.target.value })} />
                        </Field>

                        <Field label="Profile Managed By">
                          <Input value={editForm.profile_managed_by || ""} onChange={(e) => setEditForm({ ...editForm, profile_managed_by: e.target.value })} />
                        </Field>

                        <Field label="Password">
                          <div className="relative">
                            <Input 
                              type={showPasswordEdit ? "text" : "password"} 
                              value={editForm.password || ""} 
                              onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} 
                              placeholder={editForm.password_plain ? "User password" : "*(Encrypted) - Type to change*"} 
                              className="pr-10"
                            />
                            <button
                              type="button"
                              onClick={() => setShowPasswordEdit(!showPasswordEdit)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#6B1A24] transition-colors"
                              title={showPasswordEdit ? "Hide password" : "Show password"}
                            >
                              {showPasswordEdit ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                            </button>
                          </div>
                        </Field>
                      </div>

                      <Field label="About User Statement">
                        <textarea 
                          value={editForm.about || ""} 
                          onChange={(e) => setEditForm({ ...editForm, about: e.target.value })}
                          className="w-full min-h-[100px] p-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6B1A24]/40"
                          placeholder="Tell us about the candidate..."
                        />
                      </Field>

                      <div className="flex justify-end gap-2 shrink-0 border-t border-slate-100 pt-4 mt-6">
                        <Button variant="outline" onClick={() => setModalTab("view")}>Cancel</Button>
                        <Button onClick={saveProfile} disabled={savingProfile} className="bg-[#6B1A24] hover:bg-[#52141C]">
                          {savingProfile ? "Saving Changes..." : "Save Changes"}
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Tab Content: Send Message */}
                  {modalTab === "message" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 divide-y md:divide-y-0 md:divide-x divide-slate-100">
                      
                      {/* Left: Email Option */}
                      <div className="space-y-4 pr-0 md:pr-4">
                        <h4 className="font-semibold text-sm text-[#2A0A0E] flex items-center gap-2 mb-2">
                          <Mail className="w-4 h-4 text-[#6B1A24]" /> Send Email Notification
                        </h4>
                        
                        <Field label="Email Recipient Address">
                          <Input value={viewingUserFull.email || ""} disabled className="bg-slate-50 text-slate-400" />
                        </Field>

                        <Field label="Email Subject Header">
                          <Input value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="e.g. Profile Verification Approved" />
                        </Field>

                        <Field label="Email Message Body (HTML supported)">
                          <textarea 
                            value={emailBody} 
                            onChange={(e) => setEmailBody(e.target.value)}
                            className="w-full min-h-[180px] p-3 text-xs font-mono border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6B1A24]/40"
                            placeholder="<p>Dear Candidate,</p><p>We have updated your profile details...</p>"
                          />
                        </Field>

                        <Button 
                          onClick={sendEmail} 
                          disabled={sendingComm || !viewingUserFull.email} 
                          className="w-full bg-[#6B1A24] hover:bg-[#52141C] flex items-center justify-center gap-2"
                        >
                          <Send className="w-3.5 h-3.5" />
                          {sendingComm ? "Transmitting..." : "Transmit Email"}
                        </Button>
                      </div>

                      {/* Right: SMS Option */}
                      <div className="space-y-4 pt-6 md:pt-0 pl-0 md:pl-8">
                        <h4 className="font-semibold text-sm text-[#2A0A0E] flex items-center gap-2 mb-2">
                          <MessageSquare className="w-4 h-4 text-[#6B1A24]" /> Send SMS Notification
                        </h4>

                        <Field label="Phone Recipient Number">
                          <Input value={viewingUserFull.phone || ""} disabled className="bg-slate-50 text-slate-400" />
                        </Field>

                        <Field label="SMS Message Text (Plain text)">
                          <textarea 
                            value={smsMessage} 
                            onChange={(e) => setSmsMessage(e.target.value)}
                            maxLength={160}
                            className="w-full min-h-[120px] p-3 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#6B1A24]/40"
                            placeholder="e.g. Your verification OTP has been confirmed by administration. Welcome to Jharkhand Jodi!"
                          />
                          <p className="text-[10px] text-right text-slate-400 mt-1">{smsMessage.length}/160 characters</p>
                        </Field>

                        <Button 
                          onClick={sendSMS} 
                          disabled={sendingComm || !viewingUserFull.phone} 
                          className="w-full bg-[#6B1A24] hover:bg-[#52141C] flex items-center justify-center gap-2"
                        >
                          <Send className="w-3.5 h-3.5" />
                          {sendingComm ? "Transmitting..." : "Transmit SMS"}
                        </Button>
                      </div>

                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Controls Panel Footer */}
            <div className="shrink-0 bg-slate-50 px-6 py-4 border-t border-slate-100 flex flex-wrap items-center justify-between gap-3">
              {viewingUserFull ? (
                <>
                  <div className="flex flex-wrap gap-2">
                    {/* Verified state toggle */}
                    {!viewingUserFull.is_verified ? (
                      <Button size="sm" onClick={() => act(viewingUserFull.user_id, "verify")} className="bg-emerald-600 hover:bg-emerald-700">
                        <ShieldCheck className="w-3.5 h-3.5 mr-1" /> Mark Verified
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => act(viewingUserFull.user_id, "unverify")} className="text-emerald-600 hover:bg-emerald-50 border-emerald-200">
                        <BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verified
                      </Button>
                    )}

                    {/* Founding badge toggle */}
                    {!viewingUserFull.is_founding_member ? (
                      <Button size="sm" onClick={() => act(viewingUserFull.user_id, "founding")} className="bg-amber-600 hover:bg-amber-700">
                        <Award className="w-3.5 h-3.5 mr-1" /> Add Founding Badge
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => act(viewingUserFull.user_id, "unfounding")} className="text-amber-600 hover:bg-amber-50 border-amber-200">
                        <Award className="w-3.5 h-3.5 mr-1" /> Founding Badge Active
                      </Button>
                    )}

                    {/* Premium tier toggle */}
                    {!viewingUserFull.is_premium ? (
                      <Button size="sm" onClick={() => act(viewingUserFull.user_id, "premium")} className="bg-[#6B1A24] hover:bg-[#52141C]">
                        <Crown className="w-3.5 h-3.5 mr-1" /> Make Premium
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => act(viewingUserFull.user_id, "unpremium")} className="text-[#6B1A24] hover:bg-[#6B1A24]/5 border-[#6B1A24]/20">
                        <Crown className="w-3.5 h-3.5 mr-1 text-[#E69500]" /> Premium
                      </Button>
                    )}

                    {/* Hide toggle */}
                    {viewingUserFull.hidden ? (
                      <Button size="sm" variant="outline" onClick={() => act(viewingUserFull.user_id, "unhide")}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Unhide Account
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => act(viewingUserFull.user_id, "hide")}>
                        <EyeOff className="w-3.5 h-3.5 mr-1" /> Hide Account
                      </Button>
                    )}

                    {/* Ban toggle */}
                    {viewingUserFull.banned ? (
                      <Button size="sm" variant="outline" onClick={() => act(viewingUserFull.user_id, "unban")} className="text-red-600 hover:bg-red-50 border-red-200">
                        Unban Account
                      </Button>
                    ) : (
                      <Button size="sm" variant="destructive" onClick={() => act(viewingUserFull.user_id, "ban")}>
                        Ban / Block User
                      </Button>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={closeModal}>Close</Button>
                </>
              ) : (
                <div>Loading controls...</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Sub-components
function IconBtn({ children, title, onClick, danger, testid }) {
  return (
    <button title={title} onClick={onClick} data-testid={testid}
      className={`p-1 rounded-md border text-slate-600 transition-colors shrink-0 ${danger ? "border-red-200 hover:bg-red-50 hover:text-red-600" : "border-slate-200 hover:bg-slate-100"}`}>
      {children}
    </button>
  );
}

function InfoRow({ label, val }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100/60 py-1 last:border-0">
      <span className="text-slate-400 font-medium">{label}</span>
      <span className="text-slate-700 font-semibold text-right truncate max-w-[150px]">{val ?? "—"}</span>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400 text-left">{label}</label>
      {children}
    </div>
  );
}

